/**
 * useUVEditor Hook
 * 
 * UV mapping editor dengan:
 * - UV transform (offset, scale, rotation)
 * - Canvas visualization
 * - Drag handles (move, scale, rotate)
 * - Wrap mode (tile, clip)
 * 
 * @param {Object} selectedMaterial - Selected material state
 * @returns {Object} UV editor state dan methods
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { drawUvMapCanvas, downloadUvMap } from '@utils/uvTransform'

export function useUVEditor(selectedMaterial) {
  const [materialUvTransform, setMaterialUvTransform] = useState({
    offsetX: 0,
    offsetY: 0,
    repeatX: 1,
    repeatY: 1,
    rotation: 0,
  })
  const [materialUvTransformMode, setMaterialUvTransformMode] = useState('translate')
  const [textureWrapMode, setTextureWrapMode] = useState('tile')
  const [textureBackgroundColor, setTextureBackgroundColor] = useState('#000000')
  const [isUvMapDragging, setIsUvMapDragging] = useState(false)
  const [uvEditMode, setUvEditMode] = useState(false)

  const uvMapCanvasRef = useRef(null)
  const uvMapCanvasCtxRef = useRef(null)
  const uvMapDragStateRef = useRef(null)
  const uvMapRafRef = useRef(null)

  // Handle UV transform
  const handleMaterialUVTransform = useCallback(
    (property, value) => {
      if (!selectedMaterial?.material?.map) return

      const texture = selectedMaterial.material.map

      setMaterialUvTransform((prev) => ({ ...prev, [property]: value }))

      if (property === 'offsetX') {
        texture.offset.x = value
      } else if (property === 'offsetY') {
        texture.offset.y = value
      } else if (property === 'repeatX') {
        texture.repeat.x = value
      } else if (property === 'repeatY') {
        texture.repeat.y = value
      } else if (property === 'rotation') {
        texture.rotation = value
      }

      texture.needsUpdate = true
      selectedMaterial.material.needsUpdate = true

      // Throttle canvas redraw
      if (uvMapRafRef.current) {
        cancelAnimationFrame(uvMapRafRef.current)
      }

      uvMapRafRef.current = requestAnimationFrame(() => {
        if (uvMapCanvasRef.current && uvMapCanvasCtxRef.current) {
          drawUvMapCanvas(
            uvMapCanvasRef.current,
            uvMapCanvasCtxRef.current,
            selectedMaterial,
            materialUvTransformMode
          )
        }
        uvMapRafRef.current = null
      })
    },
    [selectedMaterial, materialUvTransformMode]
  )

  // Direct UV update (for smooth dragging)
  const updateMaterialUVDirectly = useCallback(
    (updates) => {
      if (!selectedMaterial?.material?.map) return

      const texture = selectedMaterial.material.map

      Object.entries(updates).forEach(([property, value]) => {
        if (property === 'offsetX') {
          texture.offset.x = value
        } else if (property === 'offsetY') {
          texture.offset.y = value
        } else if (property === 'repeatX') {
          texture.repeat.x = value
        } else if (property === 'repeatY') {
          texture.repeat.y = value
        } else if (property === 'rotation') {
          texture.rotation = value
        }
      })

      texture.needsUpdate = true
      selectedMaterial.material.needsUpdate = true

      if (uvMapRafRef.current) {
        cancelAnimationFrame(uvMapRafRef.current)
      }

      uvMapRafRef.current = requestAnimationFrame(() => {
        if (
          uvMapCanvasRef.current &&
          uvMapCanvasCtxRef.current &&
          selectedMaterial?.material?.map
        ) {
          drawUvMapCanvas(
            uvMapCanvasRef.current,
            uvMapCanvasCtxRef.current,
            selectedMaterial,
            materialUvTransformMode
          )
        }
        uvMapRafRef.current = null
      })
    },
    [selectedMaterial, materialUvTransformMode]
  )

  // Handle texture wrap mode
  const handleTextureWrapMode = useCallback(
    (mode) => {
      if (!selectedMaterial?.material?.map || !window.THREE_MODULE) return

      const texture = selectedMaterial.material.map
      const THREE = window.THREE_MODULE

      setTextureWrapMode(mode)

      if (mode === 'clip') {
        texture.wrapS = THREE.ClampToEdgeWrapping
        texture.wrapT = THREE.ClampToEdgeWrapping
        texture.minFilter = THREE.LinearMipmapLinearFilter
        texture.magFilter = THREE.LinearFilter
        if (texture.generateMipmaps !== undefined) {
          texture.generateMipmaps = true
        }

        texture.repeat.x = texture.repeat.x < 0 ? -1 : 1
        texture.repeat.y = texture.repeat.y < 0 ? -1 : 1
        texture.offset.x = 0
        texture.offset.y = 0

        setMaterialUvTransform((prev) => ({
          ...prev,
          repeatX: texture.repeat.x,
          repeatY: texture.repeat.y,
          offsetX: 0,
          offsetY: 0,
        }))

        const material = selectedMaterial.material
        if (material.color && textureBackgroundColor) {
          material.color.set(textureBackgroundColor)
        }
      } else if (mode === 'tile') {
        texture.wrapS = THREE.RepeatWrapping
        texture.wrapT = THREE.RepeatWrapping
        texture.minFilter = THREE.LinearFilter
        texture.magFilter = THREE.LinearFilter
      }

      texture.needsUpdate = true
      selectedMaterial.material.needsUpdate = true

      if (uvMapCanvasRef.current && uvMapCanvasCtxRef.current) {
        drawUvMapCanvas(
          uvMapCanvasRef.current,
          uvMapCanvasCtxRef.current,
          selectedMaterial,
          materialUvTransformMode
        )
      }
    },
    [selectedMaterial, textureBackgroundColor, materialUvTransformMode]
  )

  // Sync UV transform when material changes
  useEffect(() => {
    if (selectedMaterial?.material?.map && window.THREE_MODULE) {
      const tex = selectedMaterial.material.map
      const THREE = window.THREE_MODULE

      setMaterialUvTransform({
        offsetX: tex.offset.x,
        offsetY: tex.offset.y,
        repeatX: tex.repeat.x,
        repeatY: tex.repeat.y,
        rotation: tex.rotation,
      })

      // Sync wrap mode
      if (
        tex.wrapS === THREE.ClampToEdgeWrapping &&
        tex.wrapT === THREE.ClampToEdgeWrapping
      ) {
        setTextureWrapMode('clip')
      } else if (
        tex.wrapS === THREE.RepeatWrapping &&
        tex.wrapT === THREE.RepeatWrapping
      ) {
        setTextureWrapMode('tile')
      }

      // Sync background color
      if (selectedMaterial?.material?.color) {
        const color = selectedMaterial.material.color
        const hexColor = `#${color.getHexString()}`
        setTextureBackgroundColor(hexColor)
      }
    }
  }, [selectedMaterial?.material?.map])

  return {
    materialUvTransform,
    setMaterialUvTransform,
    materialUvTransformMode,
    setMaterialUvTransformMode,
    textureWrapMode,
    setTextureWrapMode,
    textureBackgroundColor,
    setTextureBackgroundColor,
    isUvMapDragging,
    setIsUvMapDragging,
    uvEditMode,
    setUvEditMode,
    
    uvMapCanvasRef,
    uvMapCanvasCtxRef,
    uvMapDragStateRef,
    uvMapRafRef,
    
    handleMaterialUVTransform,
    updateMaterialUVDirectly,
    handleTextureWrapMode,
    downloadUvMap: () => downloadUvMap(selectedMaterial),
  }
}

